import { Component, OnInit, inject, signal } from "@angular/core";
import { Router, NavigationEnd, RouterOutlet } from "@angular/router";
import { Header } from "./shared/components/header/header";
import { Footer } from "./shared/components/footer/footer";
import { MsalBroadcastService, MsalService } from "@azure/msal-angular";
import { AuthenticationResult, EventType } from "@azure/msal-browser";
import { UsersApiService } from "./features/services/user-profile/users-api-service";
import { toSignal } from "@angular/core/rxjs-interop";
import { filter, map } from "rxjs/operators";
import { Breadcrumb } from "./shared/components/breadcrumb/breadcrumb";
import { TitleService } from "./core/services/title-service";
import { FeedbackPrompt } from "./shared/components/utils/feedback-prompt/feedback-prompt";
import { FeedbackPromptService } from "./features/services/utils/feedback-prompt-service";
import { UserStore } from "./features/store/user-store";
import { DetailedUserService } from "./features/services/user-profile/detailed-user-service";
import { FavoriteStore } from "./features/store/favorite-store";
import { Modal } from "./shared/components/utils/modal/modal";
import { ErrorModalService } from "./core/services/error-modal-service";
import { ModalService } from "./core/services/modal-service";
import { HttpErrorResponse } from "@angular/common/http";
import { ConfigService } from "./core/services/config";
import { TranslocoModule } from "@jsverse/transloco";

@Component({
  selector: "app-root",
  imports: [
    RouterOutlet,
    Header,
    Footer,
    Breadcrumb,
    FeedbackPrompt,
    Modal,
    TranslocoModule,
  ],
  templateUrl: "./app.component.html",
  styles: [
    `
      :host {
        display: block;
        min-height: 100vh;
      }

      :host.home-page {
        background-image: url("/assets/images/bg/bg-homepage.png");
        background-size: cover;
        background-position: center;
        background-repeat: no-repeat;
        background-attachment: fixed;
      }
      :host.common-pages {
        background-image: url("/assets/images/bg/bg-common-pages.png");
        background-size: cover;
        background-position: center;
        background-repeat: no-repeat;
        background-attachment: fixed;
      }
    `,
  ],
})
export class AppComponent implements OnInit {
  private readonly titleService = inject(TitleService);
  private readonly router = inject(Router);
  private readonly msal = inject(MsalService);
  private readonly msalBroadcast = inject(MsalBroadcastService);
  private readonly usersApi = inject(UsersApiService);
  private readonly feedbackPrompt = inject(FeedbackPromptService);
  private readonly configService = inject(ConfigService);
  readonly errorModalService = inject(ErrorModalService);
  readonly modalService = inject(ModalService);
  private readonly userStore = inject(UserStore);
  private readonly detailedUserService = inject(DetailedUserService);
  private readonly favoriteStore = inject(FavoriteStore);
  readonly showEnvironmentBanner = signal<boolean>(false);
  readonly environmentLabel = signal<string>("");

  readonly isErrorPage = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map((event: NavigationEnd) =>
        event.urlAfterRedirects.startsWith("/error")
      )
    ),
    { initialValue: false }
  );

  async ngOnInit(): Promise<void> {
    await this.initializeEnvironmentBanner();

    // Initialize the title service
    this.titleService.init();

    // Listen to route changes to update background image according to the page
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.updateBackgroundClass(event.url);
      });

    // Call backend loginMe after MSAL login is fully completed (popup/redirect)
    this.msalBroadcast.msalSubject$
      .pipe(
        filter((msg) => msg.eventType === EventType.LOGIN_SUCCESS),
        map((msg) => msg.payload as AuthenticationResult)
      )
      .subscribe(async (result) => {
        // Ensure active account is set
        if (!this.msal.instance.getActiveAccount()) {
          this.msal.instance.setActiveAccount(result.account);
        }
        try {
          await this.usersApi.loginMe();
          await this.initializeUserStore();
        } catch (error) {
          if (
            error instanceof HttpErrorResponse &&
            (error.error?.detail === "No active contract found" ||
              error.error?.detail === "No contract found")
          ) {
            this.router.navigate(["/error"]);
          }
        }
      });

    this.feedbackPrompt.init();

    try {
      await this.msal.instance.initialize();

      const activeAccount = this.msal.instance.getActiveAccount();

      if (activeAccount) {
        await this.initializeUserStore();
      }
    } catch (error) {
      if (
        error instanceof HttpErrorResponse &&
        (error.error?.detail === "No active contract found" ||
          error.error?.detail === "No contract found")
      ) {
        this.router.navigate(["/error"]);
      }
    }
  }

  private async initializeUserStore(): Promise<void> {
    const user = await this.detailedUserService.getUserScope();
    this.userStore.setUserScope(user);
    if (this.favoriteStore.isRetrievingTiles() === false) {
      await this.favoriteStore.loadFavoriteTiles();
    }
  }

  private async initializeEnvironmentBanner(): Promise<void> {
    try {
      await this.configService.load();

      const currentEnvironment = this.configService.getEnvironment();
      const shouldShowBanner = !this.configService.isProduction();

      this.environmentLabel.set(currentEnvironment);
      this.showEnvironmentBanner.set(shouldShowBanner);
    } catch {
      this.environmentLabel.set("");
      this.showEnvironmentBanner.set(false);
    }
  }

  closeEnvironmentBanner(): void {
    this.showEnvironmentBanner.set(false);
  }

  private updateBackgroundClass(url: string): void {
    const hostElement = document.querySelector("app-root");
    if (hostElement) {
      hostElement.classList.remove("home-page", "common-pages");

      if (url === "/" || url === "/home") {
        hostElement.classList.add("home-page");
      } else {
        hostElement.classList.add("common-pages");
      }
    }
  }

  closeErrorModal(): void {
    this.errorModalService.close();
  }
}
